import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, AlertTriangle, Upload, Mail, ChevronDown, ChevronRight, Check, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import ScheduleUploadDialog from "./ScheduleUploadDialog";
import GcDateProposalPanel from "./GcDateProposalPanel";

const fmtShift = (n) => (n == null ? "—" : n > 0 ? `+${n}d` : n < 0 ? `${n}d` : "0");

/** Schedules section: upload button, log records, comparison table, GC email. */
export default function ProjectSchedulesSection({ project }) {
  const [logs, setLogs] = useState([]);
  const [latestFile, setLatestFile] = useState(null);
  const [needsAttention, setNeedsAttention] = useState(false);
  const [propagations, setPropagations] = useState([]);
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/schedule-logs`);
      setLogs(data.logs || []);
      setLatestFile(data.latest_file || null);
      setNeedsAttention(!!data.needs_attention);
      setPropagations(data.propagations || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const alertLog = useMemo(() => logs.find((l) => l.has_compression && !l.acknowledged_at), [logs]);

  const acknowledge = async (log) => {
    try {
      await api.post(`/schedule-logs/${log.id}/acknowledge`, { by: "" });
      toast.success("Compression acknowledged");
      load();
    } catch { toast.error("Acknowledge failed"); }
  };

  // Built as a real mailto href so the user's mail client opens with a draft —
  // the app never sends anything itself.
  const mailtoFor = (log) => {
    const to = project.gc_primary_email || "";
    const affected = (log.deltas || []).filter((d) => d.compressed);
    const rows = (affected.length ? affected : (log.deltas || [])).map((d) => {
      const where = [d.scope_code || d.scope_type, d.building, d.floor, d.area].filter(Boolean).join(" / ");
      return [
        `- ${where}`,
        `    Prior duration: ${d.prior_duration_days ?? "n/a"} days   New duration: ${d.new_duration_days ?? "n/a"} days`,
        `    Prior finish: ${d.prior_finish || "n/a"}   New finish: ${d.new_finish || "n/a"}`,
      ].join("\n");
    }).join("\n");
    const body = [
      `Project: ${project.project_name}${project.job_number ? ` (#${project.job_number})` : ""}`,
      `Schedule date: ${log.schedule_date}`,
      "",
      "The latest schedule shows the following changes to our scopes:",
      "",
      rows,
      "",
      "Please confirm whether these changes are intended so we can plan labor accordingly.",
      "",
    ].join("\n");
    const subject = `Schedule Concern — ${project.project_name} — ${log.schedule_date}`;
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="space-y-3">
    <GcDateProposalPanel project={project} />
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="project-schedules-section">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <CalendarClock className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Schedules</span>
        {needsAttention && (
          <button
            data-testid="schedules-compression-badge"
            onClick={() => setExpanded(alertLog?.id)}
            title="A scope was compressed in the newest schedule — click to review"
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-white bg-red-600 hover:bg-red-700 rounded-full px-2 py-0.5"
          >
            <AlertTriangle className="w-3 h-3" /> Compression
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {latestFile && (
            <a
              href={`${window.location.origin}/api/projects/${project.id}/schedule-file`}
              data-testid="schedule-file-download"
              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
            >
              <Download className="w-3 h-3" /> {latestFile.file_name}
            </a>
          )}
          <Button
            size="sm"
            onClick={() => setOpenUpload(true)}
            data-testid="upload-schedule-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white relative"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Schedule
            {needsAttention && (
              <span
                data-testid="upload-schedule-badge"
                className="absolute -top-1.5 -right-1.5 w-4 h-4 grid place-items-center rounded-full bg-red-600 text-white text-[10px] font-bold border border-white"
                title="Schedule compression detected"
              >
                !
              </span>
            )}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-sm text-gray-500">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="px-4 py-6 text-sm text-gray-500">
          No schedules uploaded yet. Upload the GC's master schedule to record ABS start and finish dates.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {logs.map((log) => {
            const isOpen = expanded === log.id;
            const compressedCount = (log.deltas || []).filter((d) => d.compressed).length;
            return (
              <div key={log.id} data-testid={`schedule-log-${log.id}`}>
                <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap hover:bg-gray-50">
                  <button
                    onClick={() => setExpanded(isOpen ? null : log.id)}
                    data-testid={`schedule-log-toggle-${log.id}`}
                    className="flex items-center gap-2 text-sm text-gray-800"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <span className="font-medium">Schedule {log.schedule_date}</span>
                  </button>
                  <span className="text-xs text-gray-500">{log.file_name}</span>
                  <span className="text-xs text-gray-400">
                    uploaded {(log.uploaded_at || "").slice(0, 10)} · {log.mappings?.length || 0} scope rows · {log.activity_count} activities parsed
                  </span>
                  {log.has_compression && (
                    <span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 border ${
                      log.acknowledged_at
                        ? "text-gray-500 bg-gray-100 border-gray-200"
                        : "text-red-700 bg-red-50 border-red-200"
                    }`} data-testid={`schedule-log-compression-${log.id}`}>
                      {log.acknowledged_at ? "Compression acknowledged" : `Compression · ${compressedCount}`}
                    </span>
                  )}
                  <a
                    href={project.gc_primary_email ? mailtoFor(log) : "#"}
                    onClick={(e) => {
                      if (!project.gc_primary_email) {
                        e.preventDefault();
                        toast.error("Add a GC Primary Contact email on the Info tab first");
                      }
                    }}
                    data-testid={`schedule-email-gc-${log.id}`}
                    title={project.gc_primary_email ? `Opens a draft to ${project.gc_primary_email} — nothing is sent automatically` : "No GC Primary Contact email yet"}
                    className="ml-auto text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                  >
                    <Mail className="w-3 h-3" /> Email GC
                  </a>
                </div>

                {isOpen && (
                  <div className="px-4 pb-3">
                    <table className="w-full text-sm" data-testid={`schedule-comparison-${log.id}`}>
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                          <th className="text-left px-2 py-1.5 font-medium">Scope / area</th>
                          <th className="text-left px-2 py-1.5 font-medium">Prior start</th>
                          <th className="text-left px-2 py-1.5 font-medium">New start</th>
                          <th className="text-left px-2 py-1.5 font-medium">Δ start</th>
                          <th className="text-left px-2 py-1.5 font-medium">Prior finish</th>
                          <th className="text-left px-2 py-1.5 font-medium">New finish</th>
                          <th className="text-left px-2 py-1.5 font-medium">Δ finish</th>
                          <th className="text-left px-2 py-1.5 font-medium">Prior dur</th>
                          <th className="text-left px-2 py-1.5 font-medium">New dur</th>
                          <th className="text-left px-2 py-1.5 font-medium">Δ dur</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(log.deltas || []).map((d, i) => (
                          <tr
                            key={`${d.key}-${i}`}
                            data-testid={`schedule-delta-row-${d.scope_code || d.scope_type}`}
                            data-compressed={d.compressed ? "1" : "0"}
                            className={`border-t border-gray-100 ${d.compressed ? "bg-red-50" : ""}`}
                          >
                            <td className="px-2 py-1.5 font-medium text-gray-800">
                              {[d.scope_code || d.scope_type, d.building, d.floor, d.area].filter(Boolean).join(" / ")}
                              {d.is_new && <span className="ml-1.5 text-[10px] uppercase text-blue-700 bg-blue-50 border border-blue-200 rounded px-1">new</span>}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500">{d.prior_start || "—"}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-800">{d.new_start || "—"}</td>
                            <td className={`px-2 py-1.5 text-xs font-medium ${d.start_shift_days > 0 ? "text-amber-700" : d.start_shift_days < 0 ? "text-blue-700" : "text-gray-400"}`}>
                              {fmtShift(d.start_shift_days)}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500">{d.prior_finish || "—"}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-800">{d.new_finish || "—"}</td>
                            <td className={`px-2 py-1.5 text-xs font-medium ${d.finish_shift_days < 0 ? "text-red-700" : d.finish_shift_days > 0 ? "text-amber-700" : "text-gray-400"}`}>
                              {fmtShift(d.finish_shift_days)}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500">{d.prior_duration_days ?? "—"}</td>
                            <td className="px-2 py-1.5 text-xs text-gray-800">{d.new_duration_days ?? "—"}</td>
                            <td className={`px-2 py-1.5 text-xs font-medium ${d.duration_change_days < 0 ? "text-red-700" : d.duration_change_days > 0 ? "text-emerald-700" : "text-gray-400"}`}>
                              {fmtShift(d.duration_change_days)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {log.has_compression && !log.acknowledged_at && (
                      <div className="mt-2 flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => acknowledge(log)}
                          data-testid={`schedule-acknowledge-${log.id}`} className="text-gray-700">
                          <Check className="w-3.5 h-3.5 mr-1" /> Acknowledge
                        </Button>
                        <span className="text-xs text-gray-500">Highlighted rows lost duration or finish earlier than the prior schedule.</span>
                      </div>
                    )}
                    {log.acknowledged_at && (
                      <p className="mt-2 text-xs text-gray-400">Acknowledged {(log.acknowledged_at || "").slice(0, 16).replace("T", " ")}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cluster 5 — date shift propagation audit trail */}
      {propagations.length > 0 && (
        <div className="border-t border-gray-200" data-testid="date-propagation-log">
          <div className="px-4 py-2 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            Date shifts
          </div>
          <ul className="divide-y divide-gray-100">
            {(showAllShifts ? propagations : propagations.slice(0, 10)).map((p) => (
              <li key={p.id} className="px-4 py-2 text-xs text-gray-600 flex flex-wrap gap-x-2" data-testid={`date-propagation-${p.id}`}>
                <span className="text-gray-400">{(p.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-medium text-gray-800">
                  {p.field === "gc" ? "GC completion" : "Start"} {p.days > 0 ? `+${p.days}` : p.days} days
                </span>
                <span>
                  · {p.mode === "project" ? "whole job" : p.mode === "scope" ? `scope ${p.scope_type}` : "this area only"}
                </span>
                <span>· {p.rooms_affected} room{p.rooms_affected === 1 ? "" : "s"} moved</span>
                {p.by && <span>· by {p.by}</span>}
              </li>
            ))}
          </ul>
          {propagations.length > 10 && (
            <button
              data-testid="date-propagation-toggle"
              onClick={() => setShowAllShifts((v) => !v)}
              className="w-full px-4 py-1.5 text-xs text-blue-700 hover:underline text-left"
            >
              {showAllShifts ? "Show fewer" : `Show all ${propagations.length} shifts`}
            </button>
          )}
        </div>
      )}

      <ScheduleUploadDialog
        projectId={project.id}
        open={openUpload}
        onOpenChange={setOpenUpload}
        onApplied={(log) => { load(); setExpanded(log.id); }}
      />
    </div>
    </div>
  );
}
